export default function LeaseAssignmentProcess() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-file-transfer-line"></i>
            <span>Legal Process</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            The Lease Assignment Process Explained
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Understanding how leases transfer from seller to buyer is essential for a smooth closing and avoiding post-sale disputes.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">What is Lease Assignment?</h3>
            
            <p className="text-slate-700 mb-6 leading-relaxed">
              Lease assignment is the legal process by which all existing tenant leases are transferred from the seller (current landlord) to the buyer (new landlord) at closing. This transfer happens automatically by operation of law when the property deed is transferred, but proper documentation is critical to protect all parties.
            </p>

            <p className="text-slate-700 mb-6 leading-relaxed">
              The assignment means the new owner assumes all rights and obligations under the existing leases. They step into your shoes as landlord and must honor every provision of the original lease agreements, including rent amounts, lease terms, renewal options, and any special provisions or concessions.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-blue-600 text-2xl mt-1"></i>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Key Legal Principle</h4>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    Under the doctrine of "privity of estate," when property ownership transfers, the new owner automatically becomes bound by all existing leases. Tenants do not need to sign new lease agreements or consent to the transfer.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-6">Step-by-Step Assignment Process</h3>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  1
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900 mb-2">Compile Complete Lease Documentation</h4>
                  <p className="text-slate-600 text-sm mb-3">
                    Gather all original signed lease agreements, amendments, addendums, and related documents. Create a comprehensive lease abstract summarizing key terms for each unit.
                  </p>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Required Documents:</p>
                    <ul className="text-xs text-slate-600 space-y-1">
                      <li>• Original signed lease agreements</li>
                      <li>• Lease amendments and addendums</li>
                      <li>• Move-in inspection reports</li>
                      <li>• Pet agreements and additional deposits</li>
                      <li>• Parking or storage agreements</li>
                      <li>• Any side agreements or concessions</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  2
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900 mb-2">Prepare Assignment Agreement</h4>
                  <p className="text-slate-600 text-sm mb-3">
                    Your attorney will draft a formal Assignment and Assumption of Leases agreement. This document explicitly transfers all lease rights and obligations to the buyer and confirms their acceptance.
                  </p>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <i className="ri-file-text-line text-teal-600"></i>
                      <span>Legal document</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <i className="ri-quill-pen-line text-teal-600"></i>
                      <span>Attorney drafted</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  3
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900 mb-2">Transfer Security Deposits</h4>
                  <p className="text-slate-600 text-sm mb-3">
                    All security deposits must be transferred to the buyer with complete accounting. This includes interest accrued (if required by state law) and documentation of any deductions or claims.
                  </p>
                  <div className="bg-amber-50 p-3 rounded-lg">
                    <p className="text-xs text-amber-800">
                      <strong>Critical:</strong> Failure to properly transfer security deposits can result in both seller and buyer being liable to tenants for the full deposit amount.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  4
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900 mb-2">Execute Assignment at Closing</h4>
                  <p className="text-slate-600 text-sm mb-3">
                    The Assignment and Assumption agreement is signed by both parties at closing, along with the deed transfer. This creates the legal transfer of all lease obligations.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  5
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900 mb-2">Notify Tenants of Transfer</h4>
                  <p className="text-slate-600 text-sm mb-3">
                    Within the timeframe required by state law (typically 5-30 days), send written notice to all tenants informing them of the ownership change and providing new landlord contact information.
                  </p>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-xs font-semibold text-slate-700 mb-2">Notice Must Include:</p>
                    <ul className="text-xs text-slate-600 space-y-1">
                      <li>• New owner's legal name and contact information</li>
                      <li>• New address for rent payments</li>
                      <li>• Confirmation that lease terms remain unchanged</li>
                      <li>• Security deposit transfer confirmation</li>
                      <li>• Emergency contact information</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  6
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900 mb-2">Transfer Property Management Records</h4>
                  <p className="text-slate-600 text-sm">
                    Provide the buyer with all tenant files, maintenance records, correspondence history, and property management documentation to ensure continuity of operations.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/c2558fc73197d6c12a9f737bdb8315c4.jpg"
                  alt="Lease assignment process and legal documentation for apartment building sale"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-lightbulb-line text-teal-600 text-xl"></i>
                Pro Tip: Lease Assignment Checklist
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span className="text-slate-700 text-sm">All leases reviewed and organized by unit number</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span className="text-slate-700 text-sm">Security deposit accounting completed and verified</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span className="text-slate-700 text-sm">Rent roll updated with current payment status</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span className="text-slate-700 text-sm">Assignment agreement drafted and reviewed by attorney</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span className="text-slate-700 text-sm">Tenant notification letters prepared and ready to send</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-line text-teal-600 mt-0.5"></i>
                  <span className="text-slate-700 text-sm">All tenant files and records organized for transfer</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Common Lease Assignment Issues and Solutions</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-start gap-3 mb-3">
                <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                <h4 className="font-semibold text-slate-900">Issue: Missing or Incomplete Lease Documents</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Some original leases cannot be located, or lease terms are unclear or contradictory.
              </p>
              <div className="bg-white p-4 rounded-lg">
                <p className="text-xs font-semibold text-teal-600 mb-2">Solution:</p>
                <p className="text-xs text-slate-600">
                  Have tenants sign lease confirmation agreements acknowledging current terms. Document month-to-month tenancies in writing. Consider requiring new leases for units with missing documentation.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-start gap-3 mb-3">
                <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                <h4 className="font-semibold text-slate-900">Issue: Security Deposit Discrepancies</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Security deposit amounts don't match records, or deposits weren't properly held in separate accounts.
              </p>
              <div className="bg-white p-4 rounded-lg">
                <p className="text-xs font-semibold text-teal-600 mb-2">Solution:</p>
                <p className="text-xs text-slate-600">
                  Reconcile all deposits before closing. Seller may need to bring funds to closing to cover shortfalls. Document any discrepancies in the purchase agreement.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-start gap-3 mb-3">
                <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                <h4 className="font-semibold text-slate-900">Issue: Prepaid Rent Complications</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Some tenants have prepaid rent for periods extending beyond the closing date.
              </p>
              <div className="bg-white p-4 rounded-lg">
                <p className="text-xs font-semibold text-teal-600 mb-2">Solution:</p>
                <p className="text-xs text-slate-600">
                  Prorate rent at closing. Seller credits buyer for prepaid rent periods after closing. Document all prepayments in the rent roll and closing statement.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-start gap-3 mb-3">
                <i className="ri-error-warning-line text-red-600 text-2xl"></i>
                <h4 className="font-semibold text-slate-900">Issue: Special Lease Provisions</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Some leases contain unusual terms, rent concessions, or special agreements not documented in standard lease forms.
              </p>
              <div className="bg-white p-4 rounded-lg">
                <p className="text-xs font-semibold text-teal-600 mb-2">Solution:</p>
                <p className="text-xs text-slate-600">
                  Create detailed lease abstracts highlighting all special provisions. Disclose all non-standard terms to buyer during due diligence. May affect property valuation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
